/**
 * Inertia writes its whole page object into `history.state`, so a state that
 * exists but doesn't carry a component name (a string) was written — or
 * mangled — by something else.
 *
 * This matters because `isBackForwardVisit()` in @inertiajs/core only checks
 * that the state is truthy:
 *
 *     isBackForwardVisit() { return window.history.state && this.navigationType === 'back_forward' }
 *
 * A foreign state passes that check, `setPage()` runs on it, and Inertia tries
 * to resolve a component named `undefined` (ABASTOVIRTUAL-25N / -2Y3).
 */
export function isForeignInertiaState(state: unknown): boolean {
    if (state === null || state === undefined) {
        return false;
    }

    // A primitive state is never ours, and is still truthy enough to fool
    // isBackForwardVisit().
    if (typeof state !== 'object') {
        return true;
    }

    const component = (state as Record<string, unknown>).component;

    // Inertia always stores the component name as a string. A state that has
    // the key but not a string value (e.g. `component: undefined`, left behind
    // by a sloppy clone of our state) still reaches setPage() and makes Inertia
    // resolve a component named `undefined` (the events that survived the
    // first version of this guard, which only checked `'component' in state`).
    if (typeof component !== 'string') {
        return true;
    }

    // And a string that is the *word* "undefined" is the same accident one
    // step later: something interpolated our state into a template
    // (`${page.component}`) before writing it back. It passes the typeof check
    // and Inertia goes looking for `./pages/undefined.tsx`
    // (ABASTOVIRTUAL-2Z4, 18/09 06:20, boot por back/forward en el bundle que
    // ya traía los dos guards anteriores). Un nombre de página nunca es vacío
    // ni una de estas palabras, así que descartarlas no puede tapar una real.
    return NOT_A_PAGE_NAME.includes(component.trim());
}

/** Valores que nunca son el nombre de una página y sí son strings. */
const NOT_A_PAGE_NAME = ['', 'undefined', 'null', 'NaN', '[object Object]'];

/**
 * Drops a foreign history state so Inertia treats the load as a normal visit
 * and boots from the server-rendered page in `data-page`.
 *
 * Note we clear the state rather than reloading: a reload keeps the same
 * history entry — and with it the same broken state — so it would loop.
 *
 * Returns whether anything was dropped.
 */
export function dropForeignHistoryState(history: History, url: string): boolean {
    if (!isForeignInertiaState(history.state)) {
        return false;
    }

    history.replaceState(null, '', url);

    return true;
}

/**
 * Same hole, at runtime instead of boot. `dropForeignHistoryState` only
 * inspects the state the page boots with; a foreign state can also be reached
 * later through back/forward, and Inertia's popstate handler (core 1.3) does:
 *
 *     handlePopstateEvent(e) { if (e.state !== null) { resolveComponent(e.state.component) ... } }
 *
 * so it resolves a component named `undefined` all the same — the events that
 * kept arriving after the boot guard shipped were all `popstate` (ABASTOVIRTUAL-2Y3).
 *
 * Registers a capture-phase listener that swallows the popstate before Inertia
 * sees it and lets the caller recover (typically a replace-visit to the current
 * URL, which also rewrites the broken history entry with a real page state).
 * A `null` state is left to Inertia, which handles it itself. Must be called
 * before `createInertiaApp()` so it runs ahead of Inertia's own listener.
 *
 * Returns a function that removes the listener.
 */
export function guardForeignPopstate(target: EventTarget, onForeign: (state: unknown) => void): () => void {
    const listener = (event: Event) => {
        const state = (event as PopStateEvent).state;

        if (!isForeignInertiaState(state)) {
            return;
        }

        event.stopImmediatePropagation();
        onForeign(state);
    };

    target.addEventListener('popstate', listener, { capture: true });

    return () => target.removeEventListener('popstate', listener, { capture: true });
}
